import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, cn, SearchInput, Skeleton, Text } from "@athena/ui";
import { fullName, type ClientRecord } from "../helpers/client";

/** Placeholder rows while the alumni load. */
const SKELETON_ROWS = 4;

interface RecipientListProps {
    /** Everyone the broadcast can go to; do not contact is already left out. */
    recipients: ClientRecord[];
    /** Ids the sender has unchecked. */
    excluded: ReadonlySet<string>;
    /** Checks or unchecks these recipients: one row, or every row shown for select all. */
    onToggle: (clientIds: string[], included: boolean) => void;
    loading: boolean;
    /** What to say when nobody can receive it. Defaults to the broadcast email's wording. */
    emptyMessage?: string;
}

/**
 * Every recipient by name and email, each with a checkbox, so the sender can leave someone out of
 * one broadcast without touching their outreach status. Checked means they get it.
 *
 * An unchecked row turns red and says it won't receive the email, so someone unchecked by accident
 * stands out before it goes.
 *
 * Searching by name or email only narrows what is shown: whoever is unchecked stays unchecked, in
 * view or not. Select all works on what is shown, so a search then select all picks just those —
 * and with no search, that is everyone. The list scrolls on its own, filling whatever height its
 * card gives it.
 */
export function RecipientList({ recipients, excluded, onToggle, loading, emptyMessage }: RecipientListProps) {
    const { t } = useTranslation();
    const [
        search,
        setSearch,
    ] = useState<string>("");
    const byName = useMemo(() => [...recipients].sort((a, b) => fullName(a).localeCompare(fullName(b))), [recipients]);
    const query = search.trim().toLowerCase();
    const shown = query
        ? byName.filter(
              (client) =>
                  fullName(client).toLowerCase().includes(query) ||
                  Boolean(client.email?.toLowerCase().includes(query)),
          )
        : byName;
    const shownChecked = shown.filter((client) => !excluded.has(client.id)).length;
    // A dash when some but not all of what is shown is checked.
    const allState = shownChecked === 0 ? false : shownChecked === shown.length ? true : "indeterminate";

    if (loading) {
        return (
            <div className="flex flex-col gap-3" aria-label={t("admin.alumni.broadcast.loading")}>
                {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                    <Skeleton key={index} className="h-10 w-full" />
                ))}
            </div>
        );
    }

    if (byName.length === 0) {
        return (
            <Text size="sm" muted>
                {emptyMessage ?? t("admin.alumni.broadcast.noRecipients")}
            </Text>
        );
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
            {/* SearchInput grows to fill a row; in this column that would stretch it tall instead. */}
            <div className="shrink-0">
                <SearchInput
                    label={t("admin.alumni.broadcast.searchLabel")}
                    placeholder={t("admin.alumni.broadcast.searchPlaceholder")}
                    className="bg-card h-10 rounded-full"
                    value={search}
                    onValueChange={setSearch}
                />
            </div>
            {shown.length === 0 ? (
                <Text size="sm" muted>
                    {t("admin.alumni.broadcast.noSearchMatches", { search: search.trim() })}
                </Text>
            ) : (
                <label className="border-border hover:bg-muted -mx-2 flex shrink-0 cursor-pointer items-center gap-3 rounded-lg border-b px-2 py-2">
                    <Checkbox
                        checked={allState}
                        // Anything short of all checked checks the rest; all checked unchecks them.
                        onCheckedChange={() =>
                            onToggle(
                                shown.map((client) => client.id),
                                allState !== true,
                            )
                        }
                    />
                    <Text as="span" size="sm" weight="medium" className="flex-1">
                        {query
                            ? t("admin.alumni.broadcast.selectAllShown", { count: shown.length })
                            : t("admin.alumni.broadcast.selectAll")}
                    </Text>
                    <Text as="span" size="xs" muted className="shrink-0">
                        {t("admin.alumni.broadcast.selectedCount", { selected: shownChecked, count: shown.length })}
                    </Text>
                </label>
            )}
            <ul className="-mx-2 flex max-h-96 min-h-0 flex-col overflow-y-auto lg:max-h-none lg:flex-1">
                {shown.map((client) => {
                    const unchecked = excluded.has(client.id);
                    return (
                        <li key={client.id}>
                            <label className="hover:bg-muted flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2">
                                <Checkbox
                                    checked={!unchecked}
                                    onCheckedChange={(checked) => onToggle([client.id], checked === true)}
                                    className={unchecked ? "border-destructive" : undefined}
                                />
                                <span className="min-w-0 flex-1">
                                    <Text
                                        as="span"
                                        size="sm"
                                        weight="medium"
                                        className={cn("block truncate", unchecked && "text-destructive")}
                                    >
                                        {fullName(client)}
                                    </Text>
                                    <Text as="span" size="xs" muted className="block truncate">
                                        {client.email}
                                    </Text>
                                </span>
                                {unchecked ? (
                                    <Text as="span" size="xs" weight="medium" className="text-destructive shrink-0">
                                        {t("admin.alumni.broadcast.wontReceive")}
                                    </Text>
                                ) : null}
                            </label>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
